import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Sparkles, Save, KeyRound, ShieldCheck, RefreshCw, Loader2, Cpu } from 'lucide-react';

const PROVIDERS = [
  { value: 'emergent', label: 'Emergent (managed, no key needed)' },
  { value: 'openai', label: 'OpenAI (direct key)' },
  { value: 'anthropic', label: 'Anthropic (direct key)' },
  { value: 'gemini', label: 'Google Gemini (direct key)' },
  { value: 'openrouter', label: 'OpenRouter (BYOK — activates in rollout)' },
];
const SUB_PROVIDERS = [
  { value: 'openai', label: 'OpenAI (gpt-5.4)' },
  { value: 'anthropic', label: 'Anthropic (claude-sonnet-4-6)' },
  { value: 'gemini', label: 'Gemini (gemini-3.1-pro-preview)' },
];

export default function AISettings() {
  const [settings, setSettings] = useState(null);
  const [provider, setProvider] = useState('emergent');
  const [subProvider, setSubProvider] = useState('openai');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [demoMode, setDemoMode] = useState(null);
  const [demoSaving, setDemoSaving] = useState(false);

  const loadDemo = useCallback(async () => {
    try {
      const res = await api.get('/public/demo-mode');
      setDemoMode(res.data.enabled);
    } catch (e) { /* noop */ }
  }, []);

  const toggleDemo = async () => {
    setDemoSaving(true);
    try {
      const res = await api.put('/admin/demo-mode', { enabled: !demoMode });
      setDemoMode(res.data.enabled);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update demo mode.');
    } finally {
      setDemoSaving(false);
    }
  };

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get('/ai/settings');
      setSettings(res.data);
      setProvider(res.data.provider || 'emergent');
      setSubProvider(res.data.sub_provider || 'openai');
      setModel(res.data.model || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load AI settings.');
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await api.get('/ai/audit', { params: { limit: 30 } });
      setLogs(res.data.logs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load audit log.');
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => { loadSettings(); loadLogs(); loadDemo(); }, [loadSettings, loadLogs, loadDemo]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { provider, sub_provider: subProvider, model: model || undefined };
      if (provider !== 'emergent' && apiKey.trim()) body.api_key = apiKey.trim();
      const res = await api.put('/ai/settings', body);
      toast.success(res.data.message || 'Saved.');
      if (res.data.note) toast.info(res.data.note);
      setApiKey('');
      loadSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">AI Settings (BYOK)</h1>
          <p className="text-xs text-slate-500">Choose the AI provider powering the platform. Bring your own key, or use the managed Emergent key.</p>
        </div>
      </div>

      {/* Demo data toggle */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${demoMode ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
              <Cpu className={`w-5 h-5 ${demoMode ? 'text-amber-600' : 'text-slate-500'}`} />
            </div>
            <div>
              <div className="font-semibold text-slate-900 dark:text-white">Demo Data {demoMode ? 'ON' : 'OFF'}</div>
              <p className="text-xs text-slate-500 max-w-md">
                When ON, sample doctors, hospitals and treatments are shown across the site for testing.
                Turn OFF in production to hide all demo (is_demo) records instantly.
              </p>
            </div>
          </div>
          <Button onClick={toggleDemo} disabled={demoSaving || demoMode === null}
            className={demoMode ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
            data-testid="demo-mode-toggle">
            {demoSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {demoMode ? 'Hide demo data' : 'Show demo data'}
          </Button>
        </CardContent>
      </Card>

      {/* Current status */}
      {settings && (
        <Card>
          <CardContent className="p-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-violet-600" />
              <span className="text-sm text-slate-600 dark:text-slate-300">Active provider:</span>
              <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">{settings.provider}</Badge>
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300">Model: <span className="font-medium">{settings.model || 'default'}</span></div>
            <div className="flex items-center gap-1 text-sm">
              <KeyRound className="w-4 h-4 text-slate-400" />
              {settings.key_configured ? <span className="text-emerald-600">Key configured</span> : <span className="text-slate-500">Managed key (no BYOK)</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Config form */}
      <Card>
        <CardHeader><CardTitle className="text-base">Configure provider</CardTitle></CardHeader>
        <CardContent className="p-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-500">Provider</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}
              className="mt-1 w-full text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2">
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          {provider === 'emergent' && (
            <div>
              <label className="text-xs font-medium text-slate-500">Model family (managed)</label>
              <select value={subProvider} onChange={(e) => setSubProvider(e.target.value)}
                className="mt-1 w-full text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2">
                {SUB_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          )}

          {provider !== 'emergent' && (
            <>
              <div>
                <label className="text-xs font-medium text-slate-500">Model (optional — uses provider default if blank)</label>
                <input value={model} onChange={(e) => setModel(e.target.value)}
                  placeholder="e.g. gpt-5.4 / claude-sonnet-4-6 / gemini-3.1-pro-preview"
                  className="mt-1 w-full text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">API key {settings?.key_configured ? '(leave blank to keep existing)' : ''}</label>
                <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Paste provider / OpenRouter key…"
                  className="mt-1 w-full text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2" />
                <p className="text-[11px] text-slate-400 mt-1">Keys are encrypted at rest. OpenRouter is stored now and activates in the BYOK rollout phase.</p>
              </div>
            </>
          )}

          <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save settings
          </Button>
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-violet-600" /> AI Usage Audit</CardTitle>
          <Button onClick={loadLogs} variant="outline" className="h-8 text-xs">
            {loadingLogs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </CardHeader>
        <CardContent className="p-4">
          {logs.length === 0 ? (
            <p className="text-xs text-slate-500">No AI activity recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-1.5 pr-3">Time</th>
                    <th className="py-1.5 pr-3">User</th>
                    <th className="py-1.5 pr-3">Purpose</th>
                    <th className="py-1.5 pr-3">Model</th>
                    <th className="py-1.5 pr-3">OK</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3 whitespace-nowrap text-slate-500">{(l.timestamp || '').slice(0, 19).replace('T', ' ')}</td>
                      <td className="py-1.5 pr-3">{l.user_email || '—'}<span className="text-slate-400"> ({l.role})</span></td>
                      <td className="py-1.5 pr-3"><Badge variant="outline">{l.purpose}</Badge></td>
                      <td className="py-1.5 pr-3">{l.model || '—'}</td>
                      <td className="py-1.5 pr-3">{l.success ? <span className="text-emerald-600">✓</span> : <span className="text-red-500">✕</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
